import { type KeyboardEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError, type ApiCase, type ApiCustomer, searchGlobal } from "../lib/api";
import { Badge, Card, EmptyState, Input, Modal, Skeleton } from "./ui";

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
}

type SearchItem =
  | { kind: "case"; value: ApiCase }
  | { kind: "customer"; value: ApiCustomer };

interface SearchState {
  cases: ReadonlyArray<ApiCase>;
  customers: ReadonlyArray<ApiCustomer>;
  loading: boolean;
  error: string | null;
}

const initialState: SearchState = {
  cases: [],
  customers: [],
  loading: false,
  error: null,
};

export function GlobalSearch({ open, onClose }: GlobalSearchProps): JSX.Element | null {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [state, setState] = useState<SearchState>(initialState);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const items = useMemo(() => flattenResults(state), [state]);

  useSearchResults(open, query, setState, setSelectedIndex);
  useEffect(() => setSelectedIndex((index) => clampIndex(index, items.length)), [items.length]);

  function openPath(path: string): void {
    onClose();
    void navigate(path);
  }

  function goToSelected(): void {
    const item = items[selectedIndex];
    if (item !== undefined) openPath(itemPath(item));
  }

  return (
    <Modal open={open} title="Search" onClose={onClose} className="max-w-2xl p-0">
      <div className="space-y-4 p-4">
        <Input
          autoFocus
          value={query}
          placeholder="Search cases and customers"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => handleKeyDown(event, items.length, setSelectedIndex, goToSelected)}
        />
        <SearchBody
          query={query}
          state={state}
          selectedIndex={selectedIndex}
          onSelect={setSelectedIndex}
          onOpenPath={openPath}
        />
      </div>
    </Modal>
  );
}

function useSearchResults(
  open: boolean,
  query: string,
  setState: (state: SearchState) => void,
  setSelectedIndex: (index: number) => void,
): void {
  useEffect(() => {
    const trimmed = query.trim();
    if (!open || trimmed.length === 0) {
      setState(initialState);
      setSelectedIndex(0);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setState({ ...initialState, loading: true });
      searchGlobal(trimmed)
        .then((result) => {
          if (!cancelled) setState({ ...result, loading: false, error: null });
        })
        .catch((error: unknown) => {
          if (!cancelled) setState({ ...initialState, error: searchErrorMessage(error) });
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, query, setSelectedIndex, setState]);
}

function SearchBody({
  query,
  state,
  selectedIndex,
  onSelect,
  onOpenPath,
}: {
  query: string;
  state: SearchState;
  selectedIndex: number;
  onSelect: (index: number) => void;
  onOpenPath: (path: string) => void;
}): JSX.Element {
  const hasQuery = query.trim().length > 0;
  const total = state.cases.length + state.customers.length;
  if (!hasQuery) return <EmptyState title="Type a keyword" description="Search by case title, property address, or customer name." />;
  if (state.loading) return <SearchSkeleton />;
  if (state.error !== null) return <EmptyState title="Search failed" description={state.error} />;
  if (total === 0) return <EmptyState title="No results" description="Try another keyword." />;
  return (
    <div className="space-y-4">
      <CaseGroup offset={0} items={state.cases} selectedIndex={selectedIndex} onSelect={onSelect} onOpenPath={onOpenPath} />
      <CustomerGroup offset={state.cases.length} items={state.customers} selectedIndex={selectedIndex} onSelect={onSelect} onOpenPath={onOpenPath} />
    </div>
  );
}

function CaseGroup({
  offset,
  items,
  selectedIndex,
  onSelect,
  onOpenPath,
}: ResultGroupProps<ApiCase>): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold text-text-tertiary">Cases</h3>
      <div className="space-y-2">
        {items.map((item, index) => (
          <ResultButton
            key={item.id}
            selected={selectedIndex === offset + index}
            onSelect={() => onSelect(offset + index)}
            onOpen={() => onOpenPath(`/cases/${item.id}`)}
          >
            <CaseResult item={item} />
          </ResultButton>
        ))}
      </div>
    </section>
  );
}

function CustomerGroup({
  offset,
  items,
  selectedIndex,
  onSelect,
  onOpenPath,
}: ResultGroupProps<ApiCustomer>): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold text-text-tertiary">Customers</h3>
      <div className="space-y-2">
        {items.map((item, index) => (
          <ResultButton
            key={item.id}
            selected={selectedIndex === offset + index}
            onSelect={() => onSelect(offset + index)}
            onOpen={() => onOpenPath(`/customers/${item.id}`)}
          >
            <CustomerResult item={item} />
          </ResultButton>
        ))}
      </div>
    </section>
  );
}

interface ResultGroupProps<T> {
  offset: number;
  items: ReadonlyArray<T>;
  selectedIndex: number;
  onSelect: (index: number) => void;
  onOpenPath: (path: string) => void;
}

function ResultButton({
  selected,
  onSelect,
  onOpen,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  onOpen: () => void;
  children: JSX.Element;
}): JSX.Element {
  return (
    <button
      type="button"
      onMouseEnter={onSelect}
      onClick={() => {
        onSelect();
        onOpen();
      }}
      className={cx(
        "w-full rounded-md border p-3 text-left transition",
        selected ? "border-primary bg-primary-bg" : "border-border bg-surface hover:bg-bg",
      )}
    >
      {children}
    </button>
  );
}

function CaseResult({ item }: { item: ApiCase }): JSX.Element {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-text-primary">{item.title}</p>
        <Badge variant="primary">{item.status}</Badge>
      </div>
      <p className="truncate text-xs text-text-secondary">{item.property_address ?? "No address"}</p>
    </div>
  );
}

function CustomerResult({ item }: { item: ApiCustomer }): JSX.Element {
  const contact = item.email ?? item.phone ?? item.line_id ?? "No contact";
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-text-primary">{item.name}</p>
        <Badge>Customer</Badge>
      </div>
      <p className="truncate text-xs text-text-secondary">{contact}</p>
    </div>
  );
}

function SearchSkeleton(): JSX.Element {
  return (
    <Card className="space-y-3">
      {[0, 1, 2].map((key) => (
        <div key={key} className="space-y-2">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ))}
    </Card>
  );
}

function handleKeyDown(
  event: KeyboardEvent<HTMLInputElement>,
  itemCount: number,
  setSelectedIndex: (updater: (index: number) => number) => void,
  onEnter: () => void,
): void {
  if (event.key === "ArrowDown") moveSelection(event, itemCount, setSelectedIndex, 1);
  if (event.key === "ArrowUp") moveSelection(event, itemCount, setSelectedIndex, -1);
  if (event.key === "Enter") {
    event.preventDefault();
    onEnter();
  }
}

function moveSelection(
  event: KeyboardEvent<HTMLInputElement>,
  itemCount: number,
  setSelectedIndex: (updater: (index: number) => number) => void,
  delta: number,
): void {
  if (itemCount === 0) return;
  event.preventDefault();
  setSelectedIndex((index) => (index + delta + itemCount) % itemCount);
}

function flattenResults(state: SearchState): ReadonlyArray<SearchItem> {
  return [
    ...state.cases.map((value) => ({ kind: "case" as const, value })),
    ...state.customers.map((value) => ({ kind: "customer" as const, value })),
  ];
}

function itemPath(item: SearchItem): string {
  return item.kind === "case" ? `/cases/${item.value.id}` : `/customers/${item.value.id}`;
}

function clampIndex(index: number, length: number): number {
  if (length === 0) return 0;
  return Math.min(index, length - 1);
}

function searchErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Please try again later.";
}

function cx(...classes: Array<string | false | undefined>): string {
  return classes.filter(Boolean).join(" ");
}
